import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Edit from '@mui/icons-material/Edit';
import { MemoSingleChoice } from 'components/common/simpleInputFields/singleChoice';
import { Spinner } from 'components/core';
import ViewWrapper from 'components/core/Layout/ViewWrapper';
import { FieldWithState } from 'components/fieldVisibility/BoxWithState';
import { DialogButtons } from 'components/general/ButtonsPanel/ButtonsPanel';
import { SubmitFooter } from 'components/submitFooter';
import { Fragment, ReactElement, useMemo } from 'react';
import { FormattedMessage, formatTranslation, unsafeTranslationKey } from 'intl';
import { FieldTranslationKeys, WorkflowTranslationKeys } from 'intl/keys';
import { useIntl } from 'react-intl';
import { getIssueFormSchemaWithRemovedUnknown } from 'shared/domain/issueForm/getIssueFormSchemaWithRemovedUnknown';
import { IssueFieldNames } from 'shared/domain/issueForm/types/fieldNames';
import { Process } from 'shared/domain/process/types/model';
import { Field } from 'shared/domain/visibleField/availableFields';
import { VisibleFieldModel } from 'shared/domain/visibleField/visibleFieldModel';
import { HashMap, LabelledEntity } from 'shared/types/commonView';
import { addField, addFieldWithDependsOn, isFieldVisible } from './helpers';
import { useStyles } from './styles';
import type { FieldOnView, FieldsProps, FieldVisibilityPresentationalProps } from './types';

const SPLIT_CHAR = ',';

function getStoredDefaultDays(
  fieldVisibility: HashMap<VisibleFieldModel[]>,
  processId: string,
  fieldName: string
): number | undefined {
  const visibleFields = fieldVisibility[processId] || [];
  const found = visibleFields.find(
    (vf) => vf.fieldName === fieldName && !vf.deleted
  );
  return found?.defaultDays;
}

function findVisibleSourceField(
  fields: Field[],
  dependsOnField: IssueFieldNames,
  fieldVisibility: HashMap<VisibleFieldModel[]>,
  selectedProcessId: Process
): boolean {
  const sourceField = fields.find((f) => f.fieldName === dependsOnField);
  if (!sourceField) return false;
  return isFieldVisible({
    field: sourceField,
    fieldVisibility,
    selectedProcessId,
  });
}

function isSourceFieldVisible(
  fields: Field[],
  dependsOn: IssueFieldNames | IssueFieldNames[],
  fieldVisibility: HashMap<VisibleFieldModel[]>,
  selectedProcessId: Process
): boolean {
  return Array.isArray(dependsOn)
    ? dependsOn.every((dependsOnField) =>
        findVisibleSourceField(
          fields,
          dependsOnField,
          fieldVisibility,
          selectedProcessId
        )
      )
    : findVisibleSourceField(
        fields,
        dependsOn,
        fieldVisibility,
        selectedProcessId
      );
}

function Fields({
  fields,
  settersRef,
  formState,
  defaultDaysState,
  onChange,
  onDefaultDaysChange,
  selectedProcess,
  fieldVisibility,
  loading,
}: FieldsProps): ReactElement {
  const intl = useIntl();

  const { schema } = getIssueFormSchemaWithRemovedUnknown(
    fields,
    selectedProcess._id
  );

  const fieldOnViewMap: HashMap<FieldOnView> = useMemo(() => {
    if (loading) return {};
    const fieldsOnView: HashMap<FieldOnView> = fields.reduce(
      (result, field) => {
        if (field.dependsOn) {
          const sourceFieldVisible = isSourceFieldVisible(
            fields,
            field.dependsOn,
            fieldVisibility,
            selectedProcess._id
          );

          addFieldWithDependsOn({
            result,
            field,
            fieldVisibility,
            selectedProcessId: selectedProcess._id,
            intl,
            sourceFieldVisible,
          });
        } else {
          addField({
            result,
            field,
            fieldVisibility,
            selectedProcessId: selectedProcess._id,
            intl,
          });
        }

        return result;
      },
      {} as HashMap<FieldOnView>
    );
    return fieldsOnView;
  }, [loading, fieldVisibility, fields, intl, selectedProcess]);

  if (loading) {
    return <Spinner reason='FieldsVisibility loading' />;
  }

  return (
    <>
      {schema.map((section) => {
        return (
          <Fragment key={section.sectionTitleId}>
            <h2 style={{ alignSelf: 'flex-start' }}>
              {formatTranslation(intl, unsafeTranslationKey(section.sectionTitleId) )}
            </h2>
            {section.fields.map((fieldName) => {
              const field = fieldOnViewMap[fieldName];
              if (!field) return null;
              const formKey = `${field.fieldName}${SPLIT_CHAR}${selectedProcess._id}`;
              const isChecked =
                formState[formKey] !== undefined
                  ? formState[formKey]
                  : field.visible;
              const storedDays = getStoredDefaultDays(
                fieldVisibility,
                selectedProcess._id,
                field.fieldName
              );
              const localDays = defaultDaysState[formKey];
              const days =
                localDays !== undefined
                  ? localDays === null
                    ? undefined
                    : localDays
                  : storedDays;
              return (
                <FieldWithState
                  key={`${field.fieldName},${selectedProcess._id}`}
                  initialIsChecked={field.visible}
                  isChecked={isChecked}
                  field={field}
                  settersRef={settersRef}
                  onChange={onChange}
                  label={field.label}
                  disabled={field.disabled}
                  supportsDefaultDays={field.supportsDefaultDays}
                  defaultDays={days}
                  onDefaultDaysChange={onDefaultDaysChange}
                />
              );
            })}
          </Fragment>
        );
      })}
    </>
  );
}

export function FieldVisibilityPresentational(
  props: FieldVisibilityPresentationalProps
): ReactElement {
  const classes = useStyles();
  const intl = useIntl();
  const {
    containerRef,
    selectedProcess,
    processes,
    available,
    onProcessChange,
    workflowOptions,
    selectedWorkflowOption,
    onWorkflowChange,
    workflowCreate,
    fieldsProps,
    isPosting,
    isDirty,
    onSubmit,
    onCancel,
  } = props;

  if (!selectedProcess) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <Typography variant='h6'>No process available</Typography>
      </Box>
    );
  }

  return (
    <>
      <ViewWrapper height={56}>
        <div ref={containerRef} className={classes.root}>
          <div className={classes.column}>
            <MemoSingleChoice
              key={'select-process'}
              localisedLabel={''}
              labelId={FieldTranslationKeys.visibilitySelectProcessDropdownLabel}
              available={available}
              required={true}
              id={'select-process'}
              error={undefined}
              handleChange={onProcessChange}
              defaultValue={processes[0]}
              value={selectedProcess}
              disableClearable={true}
              dense={true}
              reserveSpaceForHelperText={true}
              issueParams={null}
            />
          </div>
          <div className={classes.fieldsContainer}>
            <Fragment key='field-visibility-workflow'>
              <h2 style={{ alignSelf: 'flex-start' }}>
                <FormattedMessage id={FieldTranslationKeys.visibilityWorkflowSectionTitle} />
              </h2>
              <Autocomplete<LabelledEntity, false, true, false>
                key={`select-workflow-${selectedProcess._id}`}
                options={workflowOptions}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option._id === value._id}
                value={selectedWorkflowOption}
                onChange={(_, value) => onWorkflowChange(_, value)}
                disabled={workflowOptions.length === 0}
                disableClearable
                fullWidth
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={formatTranslation(intl, FieldTranslationKeys.visibilityWorkflowSelectLabel)}
                    required={workflowOptions.length > 0}
                    margin="dense"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {selectedWorkflowOption && (
                            <InputAdornment position="end">
                              <Tooltip title={formatTranslation(intl, WorkflowTranslationKeys.configurationEditAria)}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    workflowCreate.onEditWorkflow();
                                  }}
                                  data-qa="workflow-edit-button"
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={workflowCreate.onOpenCreate}
                  disabled={workflowCreate.createLoading}
                  data-qa="workflow-create-open"
                >
                  <FormattedMessage id={WorkflowTranslationKeys.configurationCreateButton} />
                </Button>
              </Box>
            </Fragment>
            <Fields
              key={selectedProcess._id}
              {...fieldsProps}
              selectedProcess={selectedProcess}
            />
          </div>
        </div>
      </ViewWrapper>
      <SubmitFooter
        isPosting={isPosting}
        onSubmit={onSubmit}
        onCancel={onCancel}
        disableSubmit={!isDirty}
      />

      <Dialog
        fullWidth
        maxWidth="sm"
        open={workflowCreate.createOpen}
        onClose={workflowCreate.onCloseCreate}
      >
        <DialogTitle>
          <FormattedMessage id={WorkflowTranslationKeys.configurationDialogCreateTitle} />
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ overflow: 'auto', maxHeight: '70vh' }}>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              autoFocus
              fullWidth
              label={formatTranslation(intl, WorkflowTranslationKeys.configurationNameLabel,)}
              value={workflowCreate.createName}
              onChange={(e) => workflowCreate.onChangeCreateName(e.target.value)}
              data-qa="workflow-create-name"
            />
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions>
          <DialogButtons
            onCancel={workflowCreate.onCloseCreate}
            onSubmit={workflowCreate.onSubmitCreate}
            isPosting={workflowCreate.createLoading}
            submitDisabled={!workflowCreate.createName.trim()}
          />
        </DialogActions>
      </Dialog>
    </>
  );
}
